import { Eraser, Highlighter, Pen } from "lucide-react";
import { useCanvasDrawStyle } from "../../../../hooks/useCanvasDrawStyle";
import { EDGE_COLORS } from "../../../../nodes/shared/CanvasColorPalettes";
import { markOf } from "../../../../nodes/shared/canvasColorRoles";
import { CanvasStrokeSquigglePreview } from "../../../../nodes/shared/CanvasToolbarGraphics";
import { CANVAS_STROKE_WIDTHS } from "../../../../types/canvasNodeData";
import { DRAWING_TIPS, type DrawingTip } from "../../../../utils/drawingTips";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
} from "../../canvasToolbarComposition";
import {
  SwatchGrid,
  TileGrid,
  Tray,
  TrayDeck,
  TraySection,
} from "../CanvasTrayContent";

const TIP_ICONS: Record<DrawingTip, typeof Pen> = {
  pen: Pen,
  highlighter: Highlighter,
};

const WIDTH_NAMES: Record<number, string> = {
  1: "Thin",
  2: "Medium",
  4: "Heavy",
};

/**
 * The pen kit — the only cursor mode with anything above the rail. The
 * drawer reads bottom-up: the tool nearest the bar, then its ink, then its
 * weight. Ink dims under the eraser rather than leaving: an eraser has no
 * colour, and saying so in place beats collapsing the deck.
 */
export function DrawTray() {
  const { drawTip, drawColor, drawStrokeWidth, drawErasing, setDrawStyle } =
    useCanvasDrawStyle();

  const widths = CANVAS_STROKE_WIDTHS.map((width) => ({
    key: String(width),
    name: WIDTH_NAMES[width],
    node: (
      <CanvasStrokeSquigglePreview
        width={width}
        active={drawStrokeWidth === width}
      />
    ),
  }));

  const inks = EDGE_COLORS.map((c) => ({
    key: c.value,
    name: c.name,
    mark: c.value,
  }));

  const tools = [
    ...DRAWING_TIPS.map((tip) => {
      const Icon = TIP_ICONS[tip.key];
      return {
        key: tip.key,
        name: tip.name,
        node: (
          <Icon
            size={CANVAS_TILE_GLYPH}
            strokeWidth={CANVAS_GLYPH_STROKE}
            aria-hidden="true"
          />
        ),
      };
    }),
    {
      key: "eraser",
      name: "Eraser",
      node: (
        <Eraser
          size={CANVAS_TILE_GLYPH}
          strokeWidth={CANVAS_GLYPH_STROKE}
          aria-hidden="true"
        />
      ),
    },
  ];
  const tool = drawErasing ? "eraser" : drawTip;

  return (
    <Tray>
      <TrayDeck>
        <TraySection ariaLabel="Stroke">
          <TileGrid
            items={widths}
            value={String(drawStrokeWidth)}
            onChange={(k) => setDrawStyle({ strokeWidth: Number(k) })}
            columns={widths.length}
          />
        </TraySection>
        <TraySection ariaLabel="Ink" inert={drawErasing}>
          <SwatchGrid
            role="mark"
            items={inks}
            value={markOf(drawColor)}
            onChange={(color) => setDrawStyle({ color })}
            columns={inks.length}
            disabled={drawErasing}
          />
        </TraySection>
        <TraySection ariaLabel="Draw tool">
          <TileGrid
            items={tools}
            value={tool}
            onChange={(k) =>
              k === "eraser"
                ? setDrawStyle({ erasing: true })
                : setDrawStyle({ tip: k as DrawingTip })
            }
            columns={tools.length}
          />
        </TraySection>
      </TrayDeck>
    </Tray>
  );
}
